import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useBookings } from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { supabase } from '../../../../services/supabase';
import { selectAccommodationProperty } from '../../../../store/accommodation';

export const OverstayAlert: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;

  const clientId = booking?.client?.id ?? null;
  const today = new Date().toISOString().split('T')[0];

  const { data: clientOverstays = [] } = useQuery({
    queryKey: ['client-overstays', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data } = await supabase
        .from('accommodation_bookings')
        .select(
          'id, check_in, check_out, total_amount, accommodation_booking_rooms(accommodation_rooms(room_number))',
        )
        .eq('client_id', clientId)
        .eq('status', 'checked_in')
        .lt('check_out', today)
        .neq('id', bookingId ?? '');
      return data ?? [];
    },
    enabled: !!clientId,
  });

  if (clientOverstays.length === 0) return null;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle size={15} className="text-amber-600 animate-pulse" />
        <h4 className="text-sm font-semibold text-amber-800">Overstay Alert</h4>
      </div>
      <p className="mb-3 text-xs text-amber-700 leading-relaxed">
        This client has {clientOverstays.length} other booking
        {clientOverstays.length > 1 ? 's' : ''} that{' '}
        {clientOverstays.length > 1 ? 'have' : 'has'} not been checked out on
        time.
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-amber-200 text-left text-amber-600">
            <th className="pb-1 pr-4">Room</th>
            <th className="pb-1 pr-4">Check-In</th>
            <th className="pb-1 pr-4">Check-Out</th>
            <th className="pb-1 text-right">Total Bill</th>
          </tr>
        </thead>
        <tbody>
          {clientOverstays.map((ob: any) => (
            <tr key={ob.id} className="border-b border-amber-100 font-medium">
              <td className="py-1 pr-4 text-gray-800">
                {ob.accommodation_booking_rooms?.[0]?.accommodation_rooms
                  ?.room_number ?? '—'}
              </td>
              <td className="py-1 pr-4 text-gray-600">{ob.check_in}</td>
              <td className="py-1 pr-4 text-red-600">{ob.check_out}</td>
              <td className="py-1 text-right text-gray-800">
                {(ob.total_amount as number).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
